import { useTranslation } from 'react-i18next';
import { useSession } from '@/hooks';
import { useAsyncData } from '@/hooks';
import { core } from '@/api/client';
import { Avatar, type AvatarGender } from '@/components/ui/Avatar';
import { C1Panel, C1Section, C1Chip, C1Skeleton } from '@/components/c1';
import type { StudentIdentityCard as CardData, StudentProfileResult } from '@/types/studentProfile';
import './StudentIdentityCard.css';

function mapGender(value: string | null | undefined): AvatarGender | null {
  if (!value) return null;
  const normalised = value.trim().toLowerCase();
  if (normalised === 'm' || normalised === 'male') return 'male';
  if (normalised === 'f' || normalised === 'female') return 'female';
  return null;
}

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(locale.startsWith('fr') ? 'fr-FR' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="studentIdentity-field">
      <dt>{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );
}

function Portrait({
  profile,
  displayName,
  gender,
}: {
  profile: CardData;
  displayName: string;
  gender: AvatarGender | null;
}) {
  if (profile.photographUrl) {
    return (
      <img
        className="studentIdentity-photo"
        src={profile.photographUrl}
        alt={displayName}
        width={140}
        height={160}
      />
    );
  }
  return (
    <div className="studentIdentity-avatarWrap">
      <Avatar name={displayName} gender={gender} size={140} />
    </div>
  );
}

function CardBody({
  profile,
  sessionGender,
}: {
  profile: CardData;
  sessionGender?: AvatarGender | null;
}) {
  const { t, i18n } = useTranslation();
  const displayName =
    [profile.firstName, profile.lastName].filter(Boolean).join(' ') ||
    t('home.studentIdentity.unnamed');
  const gender = mapGender(profile.gender) ?? sessionGender ?? null;
  const studyModeLabel =
    profile.classGroup?.studyMode === 'EVENING'
      ? t('home.studentIdentity.evening')
      : profile.classGroup?.studyMode === 'DAY'
        ? t('home.studentIdentity.day')
        : null;

  return (
    <div className="studentIdentity-band">
      <Portrait profile={profile} displayName={displayName} gender={gender} />
      <div className="studentIdentity-main">
        <div className="studentIdentity-titleRow">
          <h3 className="studentIdentity-name">{displayName}</h3>
          {studyModeLabel ? <C1Chip tone="accent">{studyModeLabel}</C1Chip> : null}
        </div>
        {(profile.programme || profile.classGroup) && (
          <p className="studentIdentity-subtitle">
            {[
              profile.programme
                ? [profile.programme.name, profile.programme.award].filter(Boolean).join(' · ')
                : null,
              profile.classGroup?.name,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}
        <dl className="studentIdentity-grid">
          <Field
            label={t('home.studentIdentity.admissionNumber')}
            value={profile.admissionNumber ?? '—'}
          />
          <Field
            label={t('home.studentIdentity.matriculationNumber')}
            value={profile.matriculationNumber ?? '—'}
          />
          <Field
            label={t('home.studentIdentity.gender')}
            value={
              gender === 'female'
                ? t('home.studentIdentity.female')
                : gender === 'male'
                  ? t('home.studentIdentity.male')
                  : profile.gender ?? '—'
            }
          />
          <Field
            label={t('home.studentIdentity.dateOfBirth')}
            value={formatDate(profile.dateOfBirth, i18n.language)}
          />
          <Field label={t('home.studentIdentity.phone')} value={profile.phone ?? '—'} />
          <Field label={t('home.studentIdentity.email')} value={profile.email ?? '—'} />
          <Field
            label={t('home.studentIdentity.academicYear')}
            value={profile.academicYear?.label ?? '—'}
          />
          <Field
            label={t('home.studentIdentity.institution')}
            value={profile.institution?.name ?? '—'}
          />
        </dl>
      </div>
    </div>
  );
}

/**
 * Front-and-centre student identity on CampusOne Home.
 * Soft-fails so Insights still render when EduCore is unreachable.
 */
export function StudentIdentityCard({ institutionId }: { institutionId: string }) {
  const { t } = useTranslation();
  const { session } = useSession();
  const { state } = useAsyncData<StudentProfileResult>(
    `student-profile-${institutionId}`,
    () => core<StudentProfileResult>(`/me/student-profile?institutionId=${institutionId}`),
    Boolean(institutionId),
  );

  if (state.status === 'loading') {
    return (
      <C1Section title={t('home.studentIdentity.title')}>
        <C1Panel title={t('home.studentIdentity.title')}>
          <C1Skeleton variant="block" height={160} />
        </C1Panel>
      </C1Section>
    );
  }

  if (state.status === 'error') {
    return (
      <C1Section title={t('home.studentIdentity.title')}>
        <p className="studentIdentity-note" role="status">
          {t('home.studentIdentity.unavailable')}
        </p>
      </C1Section>
    );
  }

  const result = state.data;
  if (result.state === 'UNAVAILABLE') {
    return (
      <C1Section title={t('home.studentIdentity.title')}>
        <p className="studentIdentity-note" role="status">
          {t('home.studentIdentity.unavailable')}
        </p>
      </C1Section>
    );
  }
  if (result.state === 'UNLINKED') {
    return (
      <C1Section title={t('home.studentIdentity.title')}>
        <p className="studentIdentity-note" role="status">
          {t('home.studentIdentity.unlinked')}
        </p>
      </C1Section>
    );
  }
  if (result.state !== 'READY') return null;

  return (
    <C1Section title={t('home.studentIdentity.title')}>
      <C1Panel title={t('home.studentIdentity.panelTitle')} flush>
        <CardBody
          profile={result.profile}
          sessionGender={session?.gender as AvatarGender | null | undefined}
        />
      </C1Panel>
    </C1Section>
  );
}
